// Module-switcher target building + per-tenant module-access resolution.
//
// This is workspace-ui's OWN variant, and it is deliberate (#317 A74). The module
// UIs share app-kit's `buildModuleSwitchTargets`/`accessibleModuleKeys`
// (ADR 0026); this copy differs in one load-bearing way — it takes
// `MODULE_ROUTING` from `./module-links`, so the launcher's cross-module URL
// building and this switcher cannot disagree about where a module lives. Folding
// it into app-kit would mean dragging `module-links` there too, for one consumer.
//
// The note that used to sit here cited ADR 0014 + ADR 0001 to argue the
// duplication was REQUIRED and told the reader to keep copies in step by hand.
// That stopped being true when ADR 0026 created the app-kit tier for exactly this
// kind of app glue, and a comment that justifies a copy with a rule that no
// longer says so is how the next person makes a second unnecessary one.
import { LUMI_ICONS, type LumiModuleKey, type ModuleSwitchTarget } from '@lumi/ui';
import type { Component } from 'vue';
// The names come from app-kit's shared `lumiModules` catalogue group, resolved through
// the translator this app registers in main.ts (registerAppKitI18n).
import { moduleDescription, moduleName } from '@lumi/app-kit';
// Peer-module routing (prod path segment + local dev port) is owned by
// module-links so cross-module URL building and this switcher share one table.
import { MODULE_ROUTING } from './module-links';

/** Session params handed across to the target module (see buildModuleSwitchTargets). */
export type ModuleSwitchParams = Record<string, string | null | undefined>;

/** Hosts served by local dev servers — never path-routed. */
function isLocalHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '[::1]'
  );
}

/**
 * Build the module-switcher target list for the in-module ModuleBadge.
 *
 * Mirrors workspace-ui's `openModule`: each peer app lives on a sibling origin
 * (own dev port locally; same origin but distinct path in prod), so it cannot read
 * this app's per-origin localStorage. We forward the session (token / refreshToken /
 * tenantSlug / locale — whatever the caller supplies) as query params, consumed by
 * the target app on boot. Locally we keep the current hostname (preserving any tenant
 * subdomain like `tekay.localhost`) and swap the port; in prod we path-route under
 * the current origin — avoiding collapsing tenant hosts to bare `localhost` and
 * treating `127.0.0.1` as prod (which 404s on a path dev never serves).
 *
 * The workspace (suite homebase) is always the FIRST entry when `workspaceHref` is
 * supplied, so every module can navigate back to it via the switcher. It's skipped
 * only when there's no resolvable workspace URL (e2e/bare hosts) rather than emit a
 * dead link. The workspace is the homebase, not a gated module, so it is never
 * subject to the `accessible` filter below.
 *
 * `accessible` is the set of modules the user may open in this tenant. When it is
 * null/undefined the access map is UNKNOWN, and we list NO modules — the switcher
 * must never offer a module we can't confirm the user is entitled to.
 */
export function buildModuleSwitchTargets(
  params: ModuleSwitchParams = {},
  accessible?: LumiModuleKey[] | null,
  workspaceHref?: string | null,
): ModuleSwitchTarget[] {
  if (typeof window === 'undefined') return [];
  const { hostname, origin } = window.location;
  const local = isLocalHost(hostname);

  // Access/refresh tokens ride the URL ONLY to cross-origin targets (local dev
  // ports, e2e docker hosts), where the target app cannot read this origin's
  // localStorage. A same-origin peer (prod path routing) reads the shared
  // localStorage directly, so tokens in its URL are pure leakage — browser
  // history, server logs — which ADR 0024 forbids. Non-secret context
  // (tenantSlug, locale, …) is forwarded either way.
  const TOKEN_PARAMS = new Set(['token', 'refreshToken']);
  const withParams = (base: string): string => {
    const url = new URL(base);
    const sameOrigin = url.origin === origin;
    for (const [name, value] of Object.entries(params)) {
      if (!value) continue;
      if (sameOrigin && TOKEN_PARAMS.has(name)) continue;
      url.searchParams.set(name, value);
    }
    return url.toString();
  };

  const targets: ModuleSwitchTarget[] = [];

  if (workspaceHref) {
    targets.push({
      key: 'suite',
      label: moduleName('suite'),
      description: moduleDescription('suite'),
      // Home is a place, so its tile wears the house, not the four-squares suite glyph.
      icon: LUMI_ICONS['home'] as Component,
      href: withParams(workspaceHref),
    });
  }

  if (accessible) {
    for (const key of Object.keys(MODULE_ROUTING) as Array<keyof typeof MODULE_ROUTING>) {
      if (!accessible.includes(key)) continue;
      const { segment, devPort } = MODULE_ROUTING[key];
      const base = local
        ? `${window.location.protocol}//${hostname}:${devPort}`
        : `${origin}/${segment}`;
      targets.push({ key, label: moduleName(key), description: moduleDescription(key), href: withParams(base) });
    }
  }

  return targets;
}

// ── Per-tenant module access (faithful port of workspace-ui's launcher gate) ──

/** Fields of a user-api `GET /tenants` row that decide module access. */
export interface TenantModuleAccess {
  modules?: Array<{ moduleKey: string; status?: string }>;
  assignments?: Array<{ moduleKey: string; effect?: string; roleKey?: string | null }>;
  // The showroom (#409 M3): the seeder modules this workspace has NOT booked but
  // may open while its demo lasts (user-api's copy of master's declaration).
  // Beside `modules`, never inside: no booking row exists or is implied.
  showroomModules?: string[];
}

// user-api speaks a different module namespace than the switcher / design system
// (LumiModuleKey). Single source of truth — WorkspaceView imports this too.
export const USER_API_TO_LUMI: Record<string, LumiModuleKey> = {
  ats: 'ats',
  time_tracking: 'time',
  apprenticeship: 'apprentice',
  people: 'people',
};

/** Opened by the demo without a booking (#409 M3) — only while listed. */
function isShowroomModule(access: TenantModuleAccess, moduleKey: string): boolean {
  return (access.showroomModules || []).includes(moduleKey);
}

export function isModuleActive(access: TenantModuleAccess, moduleKey: string): boolean {
  // A showroom module is open for every member while the demo lasts; the persona
  // sheet, not the booking, decides who sees it (AppChrome / WorkspaceView
  // intersect the two).
  if (isShowroomModule(access, moduleKey)) return true;
  const mods = access.modules || [];
  if (mods.length === 0) return moduleKey === 'ats';
  return mods.some((m) => m.moduleKey === moduleKey && (m.status === 'active' || m.status === 'trial'));
}

// Canonical per-tenant module-access gate — the workspace launcher and the
// in-module switcher both resolve access through this one function.
export function isModuleAccessible(access: TenantModuleAccess, moduleKey: string): boolean {
  if (!isModuleActive(access, moduleKey)) return false;
  const assignment = (access.assignments || []).find((a) => a.moduleKey === moduleKey);
  if (assignment?.effect === 'deny') return false;
  if (assignment?.effect === 'allow') return true;
  // A showroom module has neither a policy row nor an assignment: the seeder ran
  // the module-side provisioning minus master's status writes, and it is open
  // for everyone while the demo lasts (an explicit deny above still wins,
  // exactly as it does for a booked module).
  if (isShowroomModule(access, moduleKey)) return true;
  // No "nothing configured" fallback any more (ADR 0074 amendment, #457
  // successor): access IS holding an assignment now, so no assignment and no
  // showroom means no access — never "assume everyone can see it". The module
  // default role (`DEFAULT_MODULE_ROLES`) is what used to make this fallback
  // look safe, and it materializes an assignment row at the door instead, so a
  // real member never reaches this branch with nothing to show for it; a
  // role-less member of a workspace this rule postdates does, correctly, get
  // denied.
  return false;
}

/**
 * The LumiModuleKeys the user can open in this tenant — the switcher's candidate
 * set. Returns null when there's no access payload to decide from; callers treat
 * null as "unknown" and list no modules (the switcher never offers unconfirmed
 * access).
 */
export function accessibleModuleKeys(access: TenantModuleAccess | null | undefined): LumiModuleKey[] | null {
  if (!access) return null;
  const out: LumiModuleKey[] = [];
  for (const [apiKey, lumiKey] of Object.entries(USER_API_TO_LUMI)) {
    if (isModuleAccessible(access, apiKey)) out.push(lumiKey);
  }
  return out;
}

/**
 * The LumiModuleKeys this WORKSPACE has on stage — booked (active or trial) or
 * open as showroom — whatever the signed-in user's own module access says. The
 * set a worn demo persona's seats are intersected with (ADR 0030, persona
 * decision 4 and debt (e)): the sheet decides who sees what, never master access,
 * because every module API resolves the mask on tenant membership alone. A demo
 * guest is a bare membership with no module assignment at all (ADR 0030 revision
 * #669, item 2), so intersecting with `accessibleModuleKeys` left a guest's
 * launcher and switcher empty. Never use this outside a worn persona.
 */
export function stageModuleKeys(access: TenantModuleAccess | null | undefined): LumiModuleKey[] | null {
  if (!access) return null;
  const out: LumiModuleKey[] = [];
  for (const [apiKey, lumiKey] of Object.entries(USER_API_TO_LUMI)) {
    if (isModuleActive(access, apiKey)) out.push(lumiKey);
  }
  return out;
}
